/*
 * � 2025, NTT Data Group. All rights reserved. See LICENSE.txt file in the project root for third party licenses info.
 */

import { NgClass, NgStyle } from "@angular/common";
import { Component, Input, OnChanges, OnInit, SimpleChanges } from "@angular/core";
import { takeUntil, tap } from "rxjs";
import { Pattern } from "../../models/ui/charts.model";
import { FormattedKpi, KpiData } from "../../models/ui/formatted-kpi.model";
import { MaxDecimalsPipe } from "../../pipes/max-decimals.pipe";
import { AppStateService } from "../../services/state/app-state.service";
import { roundValueUpTo } from "../../utils/utils";

@Component({
	selector: "globalictcc-kpi",
	standalone: true,
	imports: [NgStyle, NgClass],
	templateUrl: "./kpi.component.html",
	styleUrl: "./kpi.component.scss"
})
export class KpiComponent implements OnChanges, OnInit {
	@Input() public label?: string;
	@Input() public value?: string | number;
	@Input() public unit?: string;
	@Input() public color?: string;
	@Input() public pattern?: Pattern;
	@Input() public useGrouping: boolean = false;
	@Input() public maxDecimals: number = 2;

	public kpi?: FormattedKpi;

	private _isAppAccessible: boolean = false;
	private _maxDecimalsPipe = new MaxDecimalsPipe();

	constructor(private _appStateService: AppStateService) {}

	ngOnInit() {
		this._appStateService.isAppAccessible$
			.pipe(
				tap({
					next: (isAppAccessible: boolean) => (this._isAppAccessible = isAppAccessible)
				}),
				takeUntil(this._appStateService.isAppDestroyed$)
			)
			.subscribe();
	}

	ngOnChanges(changes: SimpleChanges) {
		if (
			changes["label"] ||
			changes["value"] ||
			changes["unit"] ||
			changes["color"] ||
			changes["pattern"] ||
			changes["useGrouping"]
		) {
			const { label, value, unit, color, pattern } = changes;

			this.color = color?.currentValue ?? this.color;
			this.pattern = this._isAppAccessible ? (pattern?.currentValue ?? this.pattern) : null;

			const numericValue = Number(value?.currentValue ?? this.value ?? 0);
			const roundedValue = roundValueUpTo(numericValue);
			const useGrouping = changes["useGrouping"]?.currentValue ?? this.useGrouping;
			const formattedValue = this._maxDecimalsPipe.transform(
				roundedValue,
				this.maxDecimals ?? 2,
				useGrouping
			);

			this.kpi = {
				label: label?.currentValue ?? this.label,
				data: this._formatValue(formattedValue, unit?.currentValue ?? this.unit)
			};
		}
	}

	/**
	 * Formats a given value and unit into a KpiData object.
	 *
	 * @private
	 * @param {string | number} value - The value to be formatted. Can be a string or a number.
	 * @param {string} unit - The unit associated with the value.
	 * @returns {KpiData} An object containing the formatted value before and after the comma, and the unit.
	 */
	private _formatValue(value: string | number | undefined, unit: string | undefined): KpiData {
		const str = value?.toString() ?? "";
		const dotIndex = str.indexOf(".");
		return {
			valueBeforeComma: dotIndex !== -1 ? str.slice(0, dotIndex) : str,
			valueAfterComma: dotIndex !== -1 ? str.slice(dotIndex + 1) : "",
			unit
		};
	}
}
